import fixture from '../../../../../schema/fixtures/builtin-icons.json';

/**
 * Pin and category icons (docs/PARITY-STEP-4-PLAN.md decision 6), as the desktop's `IconRef`,
 * `BuiltinIcons` and `IconResolver` name and find them. An icon field is one short string:
 * blank for none (a plain dot), "lf:castle" for a built-in icon, "img:<id>" for one
 * imported into the realm, and anything else drawn as text (an emoji or a letter). The
 * built-in icons are the desktop's own path data, written to `builtin-icons.json` by its
 * `BuiltinIconsFixtureTest`.
 */

export interface BuiltinIcon {
  readonly key: string;
  readonly ref: string;
  readonly name: string;
  readonly group: string;
  /** In a 24 by 24 box. */
  readonly path: string;
  readonly evenOdd: boolean;
}

export const GLYPH_SIZE: number = fixture.size;
export const BUILTIN_ICONS: readonly BuiltinIcon[] = fixture.icons;
const BY_KEY = new Map(BUILTIN_ICONS.map((i) => [i.key, i]));

export type IconRef = { readonly kind: 'NONE' } | { readonly kind: 'EMOJI'; readonly text: string } | { readonly kind: 'BUILTIN'; readonly key: string } | { readonly kind: 'IMAGE'; readonly id: string };

export function parseIconRef(field: string | null | undefined): IconRef {
  const value = (field ?? '').trim();
  if (value === '') return { kind: 'NONE' };
  if (value.startsWith('lf:') && value.length > 3) return { kind: 'BUILTIN', key: value.slice(3) };
  if (value.startsWith('img:') && value.length > 4) return { kind: 'IMAGE', id: value.slice(4) };
  return { kind: 'EMOJI', text: value };
}

/** A pin's own icon if it has one, else its category's (`IconRef.effective`). */
export function effectiveIcon(pinIcon: string, categoryIcon: string): IconRef {
  const own = parseIconRef(pinIcon);
  return own.kind === 'NONE' ? parseIconRef(categoryIcon) : own;
}

/** An icon imported into the realm (`map_icons`): a single-colour glyph, or a picture. */
export interface MapIcon {
  readonly id: string;
  readonly pack: string;
  readonly name: string;
  readonly kind: 'GLYPH' | 'PICTURE';
  readonly mimeType: string;
  readonly data: Uint8Array;
  readonly created: string;
}

export type ResolvedIcon =
  | { readonly kind: 'GLYPH'; readonly path: string; readonly evenOdd: boolean }
  | { readonly kind: 'PICTURE'; readonly id: string; readonly data: Uint8Array; readonly mimeType: string }
  | { readonly kind: 'TEXT'; readonly text: string };

/** Something to draw, or null for a plain dot: an icon that's gone, or a built-in this version doesn't know. */
export function resolveIcon(ref: IconRef, imported: (id: string) => MapIcon | undefined): ResolvedIcon | null {
  switch (ref.kind) {
    case 'NONE':
      return null;
    case 'EMOJI':
      return { kind: 'TEXT', text: ref.text };
    case 'BUILTIN': {
      const icon = BY_KEY.get(ref.key);
      return icon ? { kind: 'GLYPH', path: icon.path, evenOdd: icon.evenOdd } : null;
    }
    case 'IMAGE': {
      const icon = imported(ref.id);
      if (!icon) return null;
      if (icon.kind === 'GLYPH') {
        // `Glyph.decode`: "E|" or "N|" for the fill rule, then the path data.
        const stored = new TextDecoder().decode(icon.data);
        const marked = stored.length > 2 && stored[1] === '|';
        return { kind: 'GLYPH', path: marked ? stored.slice(2) : stored, evenOdd: marked && stored[0] === 'E' };
      }
      return { kind: 'PICTURE', id: icon.id, data: icon.data, mimeType: icon.mimeType };
    }
  }
}
